/*
 * ACS sticky buy bar
 * ---------------------------------------------------------------------------
 * Keeps the dark total bar in step with the option pickers, and reveals it only
 * once the real Add to cart has scrolled out of view.
 *
 * Recomputes from the checked radios against a variant table rendered by the
 * section, rather than waiting on Craft's Section Rendering API. Craft only
 * swaps a handful of ids on a variant change, so anything outside that set goes
 * stale silently — and a total that lags the option a shopper just clicked is
 * worse than no total at all.
 */
(function () {
  if (typeof window.customElements === 'undefined' || window.customElements.get('acs-buy-bar')) return;

  /** Minimal money formatter honouring the shop's own money_format. */
  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    var withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (!format) return '$' + withCommas;
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toLocaleString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(cents / 100).toString());
  }

  class AcsBuyBar extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      var raw = this.querySelector('[data-acs-variants]');
      try {
        this.variants = raw ? JSON.parse(raw.textContent) : [];
      } catch (e) {
        this.variants = [];
      }
      if (!this.variants.length) return;

      this.moneyFormat = this.getAttribute('data-money-format') || '';
      this.elTotal = this.querySelector('[data-acs-total]');
      this.elSummary = this.querySelector('[data-acs-summary]');
      this.elUnit = this.querySelector('[data-acs-unit]');
      this.elSave = this.querySelector('[data-acs-save]');
      this.submit = this.querySelector('[data-acs-submit]');

      this.form = this.findProductForm();
      // Without a form to submit, the bar is decorative and misleading — a
      // button that looks like it buys and does nothing is worse than absent.
      // It stays hidden rather than rendering a dead CTA.
      if (!this.form || !this.submit) return;
      this.submit.setAttribute('form', this.form.getAttribute('id'));

      this.onChange = this.onChange.bind(this);
      this.form.addEventListener('change', this.onChange);

      // The variant picker lives outside the <form> in some layouts, so listen
      // at the document too and filter by relevance.
      this.onDocChange = function (e) {
        if (e.target && e.target.matches && e.target.matches('input[type="radio"], select')) this.onChange();
      }.bind(this);
      document.addEventListener('change', this.onDocChange);

      this.watchAnchor();
      this.update();
    }

    /**
     * The add-to-cart form, resolved at runtime.
     *
     * It cannot be named from Liquid: Craft builds the id as
     * `product-form-<section.id>` and section.id carries the THEME id
     * (`product-form-template--18581801697376__main`), which changes every time
     * the theme is saved or published. Any hardcoded value would work until the
     * next save and then silently produce a dead button.
     *
     * Note `getAttribute('id')` rather than `.id`: the form contains an input
     * named "id" (the variant id), and named form controls shadow properties on
     * the form element — `form.id` returns that INPUT, not the id string.
     *
     * The installment form also posts to /cart/add, so match on the one that
     * actually owns the submit button.
     */
    findProductForm() {
      var forms = document.querySelectorAll('form[action*="/cart/add"]');
      for (var i = 0; i < forms.length; i++) {
        if (forms[i].querySelector('.product-form__submit') && forms[i].getAttribute('id')) return forms[i];
      }
      return null;
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('change', this.onChange);
      document.removeEventListener('change', this.onDocChange);
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Reveal once the real Add to cart is off screen. IntersectionObserver
     * rather than a scroll handler: no per-frame work, and it stays correct if
     * the page reflows (Globo's option fields change height as they populate,
     * which would invalidate any cached offset).
     */
    watchAnchor() {
      var anchor = this.form ? this.form.querySelector('.product-form__submit') : null;
      if (!anchor || typeof IntersectionObserver === 'undefined') {
        this.hidden = false;
        return;
      }
      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            // Visible anchor -> hide the bar. Also hide when the anchor is
            // BELOW the viewport (i.e. not yet reached), so the bar only
            // appears after you have scrolled past the buy button.
            var pastIt = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            self.hidden = !pastIt;
          });
        },
        { threshold: 0 }
      );
      this.observer.observe(anchor);
    }

    /** The currently selected option values, in option order. */
    selection() {
      var picked = [];
      var selects = document.querySelectorAll('variant-selects input[type="radio"]:checked, variant-selects select');
      selects.forEach(function (el) {
        picked.push(el.value);
      });
      return picked;
    }

    onChange() {
      // Craft rewrites the picker's DOM after a variant change; let it settle.
      var self = this;
      window.requestAnimationFrame(function () {
        self.update();
      });
    }

    update() {
      var picked = this.selection();
      if (picked.length < 2) return;

      var size = picked[0];
      var qtyName = picked[1];
      var qty = parseInt(qtyName, 10);

      var match = null;
      var baseline = null;
      var baseQtyName = null;

      for (var i = 0; i < this.variants.length; i++) {
        var v = this.variants[i];
        if (v.o1 !== size) continue;
        if (baseQtyName === null) baseQtyName = v.o2; // first tier at this size
        if (v.o2 === qtyName) match = v;
        if (v.o2 === baseQtyName) baseline = v;
      }
      if (!match) return;

      if (this.elTotal) this.elTotal.textContent = formatMoney(match.p, this.moneyFormat);
      if (this.elSummary) this.elSummary.textContent = size + ' · ' + qtyName;

      var unit = qty > 0 ? match.p / qty : 0;
      if (this.elUnit) {
        this.elUnit.textContent = unit > 0 ? formatMoney(unit, this.moneyFormat) + ' ea' : '';
      }

      var baseQty = baseline ? parseInt(baseline.o2, 10) : 0;
      var baseUnit = baseline && baseQty > 0 ? baseline.p / baseQty : 0;
      if (this.elSave) {
        if (baseUnit > 0 && unit > 0 && unit < baseUnit) {
          this.elSave.textContent = '-' + Math.round(((baseUnit - unit) / baseUnit) * 100) + '%';
          this.elSave.hidden = false;
        } else {
          this.elSave.hidden = true;
        }
      }
    }
  }

  window.customElements.define('acs-buy-bar', AcsBuyBar);
})();
